/* === AgAccordion — Anagogia Design System === */

import { useState, useCallback, createContext, useContext } from 'react';
import { AgCollapse } from '../AgCollapse/AgCollapse';
import styles from './AgAccordion.module.css';

/* ── Internal context ── */

interface AccordionContextValue {
  expanded: boolean;
  toggle: () => void;
}

const AccordionContext = createContext<AccordionContextValue>({
  expanded: false,
  toggle: () => {},
});

/* ── AgAccordion ── */

interface AgAccordionProps {
  /** Controlled expanded state */
  expanded?: boolean;
  /** Default expanded state (uncontrolled) */
  defaultExpanded?: boolean;
  /** Called when expanded state changes */
  onChange?: (expanded: boolean) => void;
  /** Disable the accordion */
  disabled?: boolean;
  className?: string;
  children: React.ReactNode;
}

/**
 * Panneau repliable composé d'un AgAccordionSummary et d'un AgAccordionDetails.
 * @category Affichage
 */
function AgAccordion({
  expanded: expandedProp,
  defaultExpanded = false,
  onChange,
  disabled = false,
  className,
  children,
}: AgAccordionProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  const toggle = useCallback(() => {
    if (disabled) return;
    const next = !expanded;
    if (!isControlled) setInternalExpanded(next);
    onChange?.(next);
  }, [disabled, expanded, isControlled, onChange]);

  const rootCls = [
    styles.accordion,
    expanded ? styles.expanded : undefined,
    disabled ? styles.disabled : undefined,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootCls} data-expanded={expanded}>
      <AccordionContext.Provider value={{ expanded, toggle }}>
        {children}
      </AccordionContext.Provider>
    </div>
  );
}

/* ── AgAccordionSummary ── */

interface AgAccordionSummaryProps {
  /** Icon shown at end (typically an expand icon) */
  expandIcon?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

/**
 * En-tête cliquable d'un AgAccordion.
 * @category Affichage
 */
function AgAccordionSummary({
  expandIcon,
  className,
  children,
}: AgAccordionSummaryProps) {
  const { expanded, toggle } = useContext(AccordionContext);

  const cls = [styles.summary, expanded ? styles.summaryExpanded : undefined, className]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      type="button"
      className={cls}
      onClick={toggle}
      aria-expanded={expanded}
    >
      <span className={styles.summaryContent}>{children}</span>
      {expandIcon && (
        <span className={[styles.expandIcon, expanded ? styles.expandIconRotated : undefined].filter(Boolean).join(' ')}>
          {expandIcon}
        </span>
      )}
    </button>
  );
}

/* ── AgAccordionDetails ── */

interface AgAccordionDetailsProps {
  className?: string;
  children: React.ReactNode;
}

/**
 * Contenu dépliable d'un AgAccordion.
 * @category Affichage
 */
function AgAccordionDetails({
  className,
  children,
}: AgAccordionDetailsProps) {
  const { expanded } = useContext(AccordionContext);

  const cls = [styles.details, className].filter(Boolean).join(' ');

  return (
    <AgCollapse open={expanded}>
      <div className={cls}>{children}</div>
    </AgCollapse>
  );
}

export { AgAccordion, AgAccordionSummary, AgAccordionDetails };
export type { AgAccordionProps, AgAccordionSummaryProps, AgAccordionDetailsProps };
export default AgAccordion;
